import { COUNTRY_SLUGS } from '~/constants/countries'
import {
  ONE_KB_IN_BYTES,
  ONE_MB_IN_BYTES,
} from '~/constants/file'

import type { COUNTRY_SHORTS } from '~/constants'
import type { Nullable } from '~/types'

/**
 * Ensures a trailing space before the location name unless the preposition already ends with an
 * apostrophe (e.g. `à l'`) or already ends with whitespace (locale resolvers often include it).
 *
 * @param prep The preposition to append a trailing space to.
 * @returns The preposition with a trailing space.
 *
 * @example withTrailingSpace('à') // 'à '
 */
export const withTrailingSpace = (prep: string) =>
  prep.endsWith('\'') || /\s$/.test(prep) ? prep : `${prep} `

/**
 * Converts a string to a slug.
 *
 * @param text The string to convert to a slug.
 * @returns The slug.
 *
 * @example formatAsSlug('Hello World') // hello-world
 */
export function formatAsSlug(text: string) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '') // Remove accents
    .replace(/([^\w]+|\s+)/g, '-') // Replace space and other characters by hyphen
    .replace(/--+/g, '-') // Replaces multiple hyphens by one hyphen
    .replace(/(^-+|-+$)/g, '') // Remove extra hyphens from beginning or end of the string
}

/**
 * Converts a slug to a captilalized case string.
 *
 * @param slug The slug to convert.
 * @returns The capitalized case string.
 *
 * @example formatSlugAsString('hello-world-i-am-here') // Hello World I Am Here
 */
export function formatSlugAsString(slug: string) {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Converts a PascalCase string to a regular string.
 *
 * @param pascalCase The PascalCase string to convert.
 * @returns The regular string.
 *
 * @example formatPascalCaseAsString('HelloWorld') // Hello World
 */
export function formatPascalCaseAsString(pascalCase: string) {
  return pascalCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^([A-Z])/, '$1')
}

/**
 * Converts a string to a camel case string.
 *
 * @param value The string to convert.
 * @returns The camel case string.
 *
 * @example formatAsCamelCase('hello-world') // helloWorld
 */
export function formatAsCamelCase(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[-\s]+(.)?/g, (_match, group1) => group1 ? group1.toUpperCase() : '')
}

/**
 * Converts a string to a title case string.
 *
 * @param text The string to convert.
 * @returns The title case string.
 *
 * @example formatAsTitleCase('hello world') // Hello World
 */
export function formatAsTitleCase(text: string) {
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Converts a string to a pascal case string.
 *
 * @param value The string to convert.
 * @returns The pascal case string.
 *
 * @example formatAsPascalCase('hello-world') // HelloWorld
 */
export function formatAsPascalCase(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/(?:^|[-\s]+)(.)/g, (_match, group1) => group1.toUpperCase())
}

/**
 * Removes HTML tags from a string.
 *
 * @param text The string to remove HTML tags from.
 * @returns The string without HTML tags.
 *
 * @example removeHtmlTags('<p>Hello World</p>') // Hello World
 */
export function removeHtmlTags(text: string) {
  return text.replace(/<\/?[^>]+(>|$)/g, '')
}

/**
 * Converts a firstname and lastname to initials.
 *
 * @param firstName The first name.
 * @param lastName The last name.
 * @returns The initials.
 *
 * @example formatNameAsInitials('John', 'Doe') // JD
 */
export function formatNameAsInitials(firstName?: Nullable<string>, lastName?: Nullable<string>) {
  if (!firstName) {
    return 'N/A'
  }

  const firstInitial = firstName.charAt(0).toUpperCase()
  const lastInitial = lastName?.charAt(0).toUpperCase()

  return `${firstInitial}${lastInitial ?? ''}`
}

/**
 * Converts a firstname and lastname to a username.
 *
 * @param firstName The first name.
 * @param lastName The last name.
 * @returns The username.
 *
 * @example formatNameAsUsername('John', 'Doe') // John D.
 */
export function formatNameAsUsername(firstName?: Nullable<string>, lastName?: Nullable<string>) {
  if (!firstName) {
    return 'N/A'
  }

  const lastInitial = lastName ? ` ${lastName?.charAt(0).toUpperCase()}.` : ''

  return `${firstName}${lastInitial}`
}

/**
 * Converts a fullname to initials.
 *
 * @param name The full name.
 * @returns The initials.
 *
 * @example formatFullNameAsInitials('John Doe') // JD
 */
export function formatFullNameAsInitials(name: string) {
  const nameArray = name.split(' ')
  const firstInitial = nameArray[0]?.charAt(0).toUpperCase()
  const lastInitial = nameArray.length > 1 ? nameArray[nameArray.length - 1]?.charAt(0).toUpperCase() : ''

  return `${firstInitial}${lastInitial}`
}

/**
 * Formats a first and last name as a fullname.
 *
 * @param firstName The first name.
 * @param lastName The last name.
 * @returns The full name.
 *
 * @example formatNameAsFullName('John', 'Doe') // John Doe
 */
export function formatNameAsFullName(firstName?: Nullable<string>, lastName?: Nullable<string>) {
  if (firstName && lastName) return `${firstName.trim()} ${lastName.trim()}`
  if (firstName && !lastName) return firstName.trim()
  if (!firstName && lastName) return lastName.trim()
  return ''
}

/**
 * Formats a name in possesive form.
 *
 * @param name The name to format.
 * @returns The possesive form of the name.
 *
 * @example formatNameAsPossessive('James') // James'
 * @example formatNameAsPossessive('John') // John's'
 */
export function formatNameAsPossessive(name?: Nullable<string>) {
  if (!name?.trim()) {
    return ''
  }

  const trimmed = name.trim()

  if (trimmed.toLowerCase().endsWith('s')) {
    return `${trimmed}'`
  }

  return `${trimmed}'s`
}

/**
 * Formats an RV label based on year, make, and model.
 *
 * @param year The year.
 * @param make The name of the brand.
 * @param model The name of the model.
 * @returns The formatted string.
 *
 * @example formatRvLabel(2000, 'Thor', 'Chateau') // 2000 Thor Chateau
 */
export function formatRvLabel(year?: Nullable<number>, make?: Nullable<string>, model?: Nullable<string>) {
  if (!year && !make && !model) {
    return 'Unknown RV'
  }

  return [year, make, model].filter(Boolean).join(' ')
}

/**
 * Escapes an string prepending a backslash to double quotes.
 *
 * @param name The RV name to escape.
 * @returns The escaped RV name.
 *
 * @example escapeDoubleQuotes('A "quoted" RV name') // A \"quoted\" RV name
 */
export function escapeDoubleQuotes(name: string) {
  return name.replace(/"/g, '\\"')
}

/**
 * Trims a string and collapses an empty result to null, so optional values can be omitted rather
 * than sent as blank strings.
 *
 * @param value The raw string value.
 * @returns The trimmed value, or null when it is blank.
 *
 * @example trimToNull('  hi  ') // 'hi'
 * @example trimToNull('   ') // null
 */
export function trimToNull(value: string): Nullable<string> {
  const trimmed = value.trim()

  return trimmed.length > 0 ? trimmed : null
}

/**
 * Transforms first character of a string to uppercase.
 *
 * @param text The string to transform the first letter.
 * @returns The transformed string.
 *
 * @example capitalizeFirstLetter('hello world') // Hello world
 */
export function capitalizeFirstLetter(text?: Nullable<string>) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}

/**
 * Formats a number by prepending leading zeroes based on the specified length.
 *
 * @param num The number to format.
 * @param [length] The minimum length of the formatted string. Defaults to 1.
 * @returns The formatted string.
 *
 * @example formatNumberWithLeadingZero(5) // 5
 * @example formatNumberWithLeadingZero(5, 2) // 005
 * @example formatNumberWithLeadingZero(5, 3) // 0005
 */
export function formatNumberWithLeadingZero(num: number, length: number = 0) {
  if (length < 0) {
    return num.toString()
  }

  return `${'0'.repeat((length))}${num}`
}

/**
 * Formats a number with a precision.
 *
 * @todo this is actually a conversion, we might want to make a conversions
 * utils file for these cases. (i.e. convert-strings.ts, convert-numbers.ts,
 * etc.)
 *
 * @param value The string to convert to a float.
 * @param defaultValue The fallback if parseFloat(value) isNaN.
 * @returns The float representation of string or defaultValue.
 *
 * @example toFloat('1.4', 1) // 1.4
 */
export function toFloat(value: string, defaultValue: number) {
  const float = parseFloat(value)

  return isNaN(float) ? defaultValue : float
}

/**
 * Replaces diacritics from a string.
 *
 * @param text The string to replace diacritics from.
 * @returns The string without diacritics.
 *
 * @example replaceDiacritics('Héllò') // Hello
 */
export function replaceDiacritics(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Normalizes a heading id for in-page anchor links.
 *
 * @param id The raw heading id from HTML.
 * @returns The normalized id without diacritics or URL encoding.
 *
 * @example normalizeAttributeId('caf%C3%A9') // cafe
 */
export function normalizeAttributeId(id: string) {
  let decoded = id

  try {
    decoded = decodeURIComponent(id)
  }
  catch {
    // Malformed percent sequences are left as-is.
  }

  return replaceDiacritics(decoded)
}

/**
 * Formats a bytes as a megabytes string.
 *
 * @param bytes The bytes to format.
 * @returns The formatted string.
 *
 * @example formatBytesAsMegabytes(1024 * 1024) // 1.0 MB
 */
export function formatBytesAsMegabytes(bytes: number) {
  return `${(bytes / ONE_MB_IN_BYTES).toFixed(1)} MB`
}

/**
 * Truncates a string to a maximum length.
 *
 * @param text The string to truncate.
 * @param maxLength The maximum length of the string.
 * @param [ellipsis] The ellipsis to use. Defaults to '...'.
 * @returns The truncated string.
 *
 * @example truncateString('Hello World', 5) // Hello...
 */
export function truncateString(text: string, maxLength: number, ellipsis: string = '...') {
  if (text.length <= maxLength) {
    return text
  }

  // If the character at the max length is not a space, we need to remove all
  // characters before the last space.
  const charAtMaxLength = text.charAt(maxLength)
  const lastSpaceIndex = text.lastIndexOf(' ', charAtMaxLength === ' ' ? maxLength - 1 : maxLength)

  return `${text.slice(0, lastSpaceIndex >= 0 ? lastSpaceIndex : maxLength).trimEnd()}${ellipsis}`
}

/**
 * Formats a source URL by removing the protocol and www.
 *
 * @param source The source URL to format.
 * @returns The formatted source URL.
 *
 * @example formatSourceUrl('https://www.google.com') // google.com
 */
export function formatSourceUrl(source: string) {
  return source.replace('https://', '').replace('http://', '').replace('www.', '')
}

/**
 * Converts bytes to a file size string.
 *
 * @param bytes The number of bytes to convert.
 * @returns The file size string.
 */
export function bytesToFileSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) {
    return '0 Bytes'
  }

  const i = Math.floor(Math.log(bytes) / Math.log(ONE_KB_IN_BYTES))

  if (i < sizes.length) {
    return `${Math.round(bytes / Math.pow(ONE_KB_IN_BYTES, i))} ${sizes[i]}`
  }

  return `${Math.round(bytes / Math.pow(ONE_KB_IN_BYTES, sizes.length - 1))} ${sizes[sizes.length - 1]}`
}

/**
 * Formats a country short to a country slug.
 *
 * @param countryShort The country short to format.
 * @returns The country slug.
 *
 * @example formatCountryCodeAsSlug('US') // united-states
 */
export const formatCountryCodeAsSlug = (countryShort: COUNTRY_SHORTS) => {
  return COUNTRY_SLUGS[countryShort]
}
